"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { motion, AnimatePresence } from "motion/react";
import { Check, Loader2, SkipForward } from "lucide-react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Text } from "@/app/v2/_components/text/Text";
import ShinyText from "@/src/TextAnimations/ShinyText/ShinyText";
import Counter from "@/components/animated-counter/Counter";
import { Column } from "@/app/v2/_components/column/Column";
import { Row } from "@/app/v2/_components/row/Row";

const ease = [0.16, 1, 0.3, 1] as const;
function useDebugFlag() {
  const [debug, setDebug] = useState(false);
  useEffect(() => {
    setDebug(new URLSearchParams(window.location.search).has("debug"));
  }, []);
  return debug;
}

/**
 * Dev-only hook: captures each distinct progress snapshot and lets you
 * step through them with a "Next" button instead of auto-advancing.
 */
function useDevStepper<T>(live: T | undefined, enabled: boolean) {
  const [snapshots, setSnapshots] = useState<T[]>([]);
  const [index, setIndex] = useState(0);
  const lastJson = useRef("");

  useEffect(() => {
    if (!enabled || !live) return;
    const json = JSON.stringify(live);
    if (json !== lastJson.current) {
      lastJson.current = json;
      setSnapshots((prev) => [...prev, live]);
    }
  }, [live, enabled]);

  const next = useCallback(() => {
    setIndex((i) => Math.min(i + 1, snapshots.length - 1));
  }, [snapshots.length]);

  const skip = useCallback(() => {
    setIndex(snapshots.length - 1);
  }, [snapshots.length]);

  if (!enabled) return { value: live, devBar: null };

  const current = snapshots[index] ?? live;
  const behind = snapshots.length - 1 - index;

  const devBar =
    snapshots.length > 0 ? (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg bg-black/80 text-white text-xs px-3 py-2 shadow-lg backdrop-blur">
        <span className="opacity-60">
          Step {index + 1}/{snapshots.length}
        </span>
        {behind > 0 && (
          <span className="bg-amber-500 text-foreground px-1.5 py-0.5 rounded font-bold">{behind} queued</span>
        )}
        <button
          onClick={next}
          disabled={behind === 0}
          className="bg-white/20 hover:bg-white/30 disabled:opacity-30 px-2 py-1 rounded font-medium"
        >
          Next →
        </button>
        <button
          onClick={skip}
          disabled={behind === 0}
          className="bg-white/10 hover:bg-white/20 disabled:opacity-30 px-2 py-1 rounded"
        >
          <SkipForward className="h-3 w-3" />
        </button>
      </div>
    ) : null;

  return { value: current, devBar };
}

/**
 * Smooth animated counter that interpolates between batch updates.
 * Instead of jumping 0→50→100→150 in visible steps, it continuously
 * ticks up at a natural pace with slight jitter for organic feel.
 */
/**
 * Smooth fake counter that ticks from 0 to `target` over ~15s per 1000 contacts.
 * Uses a base rate with organic jitter so it never pauses or jumps visibly.
 * Snaps to `target` once the real value reaches it (run complete).
 */
function AnimatedCounter({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const targetRef = useRef(value);
  const displayRef = useRef(value);
  const startedRef = useRef(false);
  const rafRef = useRef<number>(0);
  const lastTickRef = useRef(performance.now());

  // Track the latest real value — we never go above it
  targetRef.current = value;

  useEffect(() => {
    // If value jumps to final (e.g. returning to page with complete run), snap
    if (value > 0 && !startedRef.current) {
      displayRef.current = value;
      setDisplay(value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (value <= 0 || startedRef.current) return;
    startedRef.current = true;

    // ~15s per 1000 contacts = ~67 contacts/sec base rate
    const baseRate = Math.max(value, 1000) / 15000; // contacts per ms

    const tick = (now: number) => {
      const dt = now - lastTickRef.current;
      lastTickRef.current = now;

      // Vary speed: 0.5x to 1.5x base rate for organic feel
      const jitter = 0.5 + Math.random();
      const increment = baseRate * dt * jitter;
      const next = Math.min(displayRef.current + increment, targetRef.current);

      const rounded = Math.round(next);
      if (rounded !== Math.round(displayRef.current)) {
        setDisplay(rounded);
      }
      displayRef.current = next;

      // Keep ticking until we reach the real value
      if (rounded < targetRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(targetRef.current);
        displayRef.current = targetRef.current;
      }
    };

    lastTickRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the real value increases (new batch from server), restart ticking if stopped
  useEffect(() => {
    if (value > displayRef.current && startedRef.current) {
      const baseRate = Math.max(value, 1000) / 15000;
      const tick = (now: number) => {
        const dt = now - lastTickRef.current;
        lastTickRef.current = now;
        const jitter = 0.5 + Math.random();
        const increment = baseRate * dt * jitter;
        const next = Math.min(displayRef.current + increment, targetRef.current);
        const rounded = Math.round(next);
        if (rounded !== Math.round(displayRef.current)) setDisplay(rounded);
        displayRef.current = next;
        if (rounded < targetRef.current) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setDisplay(targetRef.current);
          displayRef.current = targetRef.current;
        }
      };
      lastTickRef.current = performance.now();
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{display.toLocaleString()}</>;
}

/**
 * Fake progress bar that resets each time `key` changes.
 * Eases toward ~90% over `duration` ms, then jumps to 100% when key changes.
 */
function useFakeProgress(key: string | null, duration = 5000) {
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    if (!key) {
      setPercent(0);
      return;
    }

    // Reset immediately on new key, then ease toward 90%
    setPercent(0);
    const start = performance.now();
    let raf: number;
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      setPercent(Math.round(90 * (1 - Math.pow(1 - t, 2))));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    // Small delay so the reset to 0 renders before climbing starts
    const startTimer = setTimeout(() => {
      raf = requestAnimationFrame(step);
    }, 50);
    return () => {
      clearTimeout(startTimer);
      cancelAnimationFrame(raf);
    };
  }, [key, duration]);

  return percent;
}

type PipelineStage = "pending" | "running" | "done";

const STAGES = [
  { key: "skipTrace", label: "Skip Trace" },
  { key: "propertyData", label: "Property" },
  { key: "equityAnalysis", label: "Equity" },
  { key: "segmentation", label: "Buckets" },
];

interface BucketerProcessingProps {
  runId: Id<"bucketerRuns">;
  onComplete: () => void;
  onBack?: () => void;
}

export function BucketerProcessing({ runId, onComplete }: BucketerProcessingProps) {
  const liveRun = useQuery(api.bucketer.queries.getRunProgress, { runId });
  const runResults = useQuery(api.bucketer.queries.getRunResults, { runId });

  const debugMode = useDebugFlag();
  const { value: run, devBar } = useDevStepper(liveRun, debugMode);
  const isComplete = run?.status === "complete";

  // Extract insight-writing state before early return so hooks are always called
  const bucketingStepRaw = (run as Record<string, unknown> | undefined)?.["bucketingStep"] as string | undefined;
  const activeSegmentNameRaw = bucketingStepRaw?.match(/"([^"]+)"/)?.[1] ?? null;
  const isWritingInsightsRaw = bucketingStepRaw?.startsWith("Writing insights") ?? false;
  const insightProgress = useFakeProgress(isWritingInsightsRaw ? activeSegmentNameRaw : null);

  const didCallComplete = useRef(false);
  useEffect(() => {
    if (isComplete && run?.status === "complete" && !didCallComplete.current) {
      didCallComplete.current = true;
      onComplete();
    }
  }, [isComplete, run?.status, onComplete]);

  if (!run) {
    return (
      <section className="h-full flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-foreground/20" />
        {devBar}
      </section>
    );
  }

  const prog = run.progress as Record<string, number | string | undefined>;
  const runAny = run as Record<string, unknown>;
  const isFailed = run.status === "failed";
  const isBucketing = run.status === "bucketing";

  const totalFromResults = runResults?.buckets?.reduce((s, b) => s + b.contactCount, 0) ?? 0;
  const totalContacts = Number(prog["totalContacts"]) || totalFromResults || 0;
  const enrichedCount = isComplete ? totalContacts : Number(prog["contactsEnriched"]) || 0;
  const enrichmentPercent = isComplete
    ? 100
    : totalContacts > 0
      ? Math.round((enrichedCount / totalContacts) * 100)
      : 0;

  const propertiesEnriched = Number(prog["propertiesLookedUp"]) || (isComplete ? totalFromResults : 0);

  const bucketingStep = (runAny["bucketingStep"] as string) || null;
  const liveBuckets = runResults?.buckets ?? [];

  const activeSegmentName = activeSegmentNameRaw;
  const isWritingInsights = isWritingInsightsRaw;

  const getStageStatus = (key: string): PipelineStage => {
    if (isComplete) return "done";
    return (prog[key] as PipelineStage) ?? "pending";
  };

  const allStagesDone = isComplete || STAGES.every((s) => getStageStatus(s.key) === "done");

  const subtitle = isFailed
    ? null
    : isComplete
      ? `Enriched ${totalContacts.toLocaleString()} contacts with property + equity data`
      : isBucketing && bucketingStep
        ? bucketingStep
        : totalContacts > 0
          ? `Enriching ${totalContacts.toLocaleString()} contacts`
          : "Loading contacts...";

  return (
    <section className="h-full flex flex-col items-center justify-start px-4 md:px-6 py-4 pt-16 overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease }}
        className="w-full max-w-2xl"
      >
        {/* Header */}
        <div className="text-center mb-6">
          {isFailed ? (
            <>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground">Something went wrong</h2>
              {run.error && (
                <p className="mt-4 text-sm text-rose-600 bg-red-50 rounded-xl px-5 py-3 inline-block max-w-md">
                  {run.error}
                </p>
              )}
            </>
          ) : isComplete ? (
            <>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease, delay: 0.1 }}
                className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"
              >
                <Check className="h-7 w-7 text-emerald-600" strokeWidth={2.5} />
              </motion.div>
              <Text xxxxl chonky>
                Your Databank is ready
              </Text>
            </>
          ) : (
            <Text xl chonky>
              Building your Databank
            </Text>
          )}
          {subtitle && (
            <AnimatePresence mode="wait">
              <motion.div
                key={subtitle}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3, ease }}
                className="mt-2"
              >
                <ShinyText
                  text={subtitle}
                  speed={4}
                  shineColorRgba="rgba(255, 102, 241, 0.9)"
                  className="text-medium text-subdued-foreground"
                />
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Main progress counter + bar */}
        {!isFailed && (
          <div className="mb-4">
            <div className="text-center mb-3">
              {enrichedCount > 0 && !isComplete && (
                <>
                  <span className="text-5xl md:text-6xl font-extrabold text-foreground tabular-nums tracking-tight justify-center flex">
                    <Row align shrink noGap>
                      {isComplete ? (
                        enrichedCount.toLocaleString()
                      ) : (
                        <Counter
                          value={enrichedCount}
                          places={Array.from(
                            { length: enrichedCount.toString().length },
                            (_, i) => 10 ** (enrichedCount.toString().length - i - 1),
                          )}
                          digitStyle={{ minWidth: "15px", width: "1.4rem" }}
                          digitHeight={32}
                          commaClassName="w-[0.5rem] -ml-1 mt-1.5"
                          fontClassName="text-[2rem] font-bold text-emerald-500 "
                          prefixStyle={{ color: "inherit", width: "fit-content" }}
                        />
                      )}
                      <Text xxl>contacts</Text>
                    </Row>
                  </span>
                </>
              )}
            </div>
            <motion.div
              className={`h-2 rounded-full ${
                isComplete
                  ? "bg-emerald-600"
                  : isWritingInsights
                    ? "bg-emerald-400"
                    : enrichedCount > 0
                      ? "bg-emerald-500"
                      : "bg-foreground/5"
              }`}
              initial={{ width: "5%" }}
              animate={{
                width: isWritingInsights
                  ? `${insightProgress}%`
                  : enrichedCount === 0 && !isComplete
                    ? "100%"
                    : `${enrichmentPercent}%`,
              }}
              transition={{ duration: isWritingInsights ? 0.1 : 0.6, ease }}
            />
          </div>
        )}

        {/* Pipeline stages */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {STAGES.map((stage) => {
            const status = getStageStatus(stage.key);
            return (
              <div
                key={stage.key}
                className={`flex flex-col items-center justify-center rounded-lg border px-2 py-2.5 text-center transition-all duration-500 h-16 ${
                  status === "done"
                    ? "border-emerald-500/40 bg-emerald-50"
                    : status === "running"
                      ? "border-primary/20 bg-primary/4"
                      : "border-transparent bg-foreground/2"
                }`}
              >
                <p
                  className={`text-xs uppercase tracking-widest font-bold leading-none mb-1.5 ${
                    status === "done"
                      ? "text-emerald-600/80"
                      : status === "running"
                        ? "text-primary/80"
                        : "text-foreground/15"
                  }`}
                >
                  {stage.label}
                </p>
                <AnimatePresence mode="wait">
                  {status === "done" ? (
                    <motion.span
                      key="d"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center justify-center gap-2 text-emerald-600 text-[11px] font-semibold"
                    >
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      Done
                    </motion.span>
                  ) : status === "running" ? (
                    <motion.span
                      key="r"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center justify-center gap-2 text-primary text-[11px] font-semibold"
                    >
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                      </span>
                      Running
                    </motion.span>
                  ) : (
                    <span className="text-[11px] text-foreground/12 font-medium">&mdash;</span>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Dynamic content area with smooth height transitions */}
        <motion.div layout transition={{ layout: { duration: 0.4, ease } }} className="min-h-[140px]">
          <AnimatePresence mode="wait">
            {/* Live bucket preview */}
            {!isComplete && !isFailed && (isBucketing || run.status === "enriching") && (
              <motion.div
                key={liveBuckets.length > 0 ? "live" : "skeleton"}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease }}
                className="rounded-xl border border-foreground/5 p-5"
              >
                {liveBuckets.length > 0 ? (
                  <>
                    <p className="text-[9px] uppercase tracking-widest font-bold text-foreground/20 mb-3">
                      Buckets defined
                    </p>
                    <div className="space-y-2">
                      {liveBuckets
                        .filter((b) => b.contactCount > 0)
                        .sort((a, b) => (b.valueSummary?.totalValue ?? 0) - (a.valueSummary?.totalValue ?? 0))
                        .slice(0, 6)
                        .map((bucket, i) => {
                          const isActive = activeSegmentName === bucket.name;
                          return (
                            <motion.div
                              key={bucket._id}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, ease, delay: i * 0.05 }}
                              className="flex items-center justify-between gap-3"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div
                                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                    isActive
                                      ? "bg-primary animate-pulse"
                                      : bucket.narrative
                                        ? "bg-emerald-400"
                                        : "bg-foreground/10"
                                  }`}
                                />
                                {isActive ? (
                                  <ShinyText
                                    text={bucket.name}
                                    speed={2}
                                    shineColorRgba="rgba(99, 102, 241, 0.9)"
                                    className="text-sm text-primary/80 truncate font-medium"
                                  />
                                ) : (
                                  <span className="text-sm text-foreground/70 truncate">{bucket.name}</span>
                                )}
                              </div>
                              <span className="text-xs text-foreground/30 shrink-0 tabular-nums">
                                {bucket.contactCount.toLocaleString()}
                              </span>
                            </motion.div>
                          );
                        })}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-[9px] uppercase tracking-[0.12em] font-bold text-foreground/20 mb-4">Preview</p>
                    <div className="grid grid-cols-3 gap-6">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="space-y-2.5">
                          <div
                            className="h-2.5 bg-foreground/4 rounded-full animate-pulse"
                            style={{ width: `${65 + i * 10}%`, animationDelay: `${i * 200}ms` }}
                          />
                          <div
                            className="h-2 bg-foreground/3 rounded-full w-full animate-pulse"
                            style={{ animationDelay: `${i * 200 + 100}ms` }}
                          />
                          <div
                            className="h-2 bg-foreground/3 rounded-full animate-pulse"
                            style={{ width: `${80 - i * 8}%`, animationDelay: `${i * 200 + 200}ms` }}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            )}
            {/* Stats — shown during processing */}
            {!isComplete &&
              !isFailed &&
              !(isBucketing || run.status === "enriching") &&
              (totalContacts > 0 || propertiesEnriched > 0) && (
                <motion.div
                  key="stats-only"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-10 pt-4"
                >
                  <div className="text-center">
                    <span className="text-2xl font-bold text-foreground tabular-nums">
                      <AnimatedCounter value={totalContacts} />
                    </span>
                    <p className="text-medium text-subdued-foreground mt-0.5">contacts loaded</p>
                  </div>
                  <div className="w-px h-8 bg-foreground/6" aria-hidden />
                  <div className="text-center">
                    <span className="text-2xl font-bold text-foreground tabular-nums">
                      <AnimatedCounter value={propertiesEnriched} />
                    </span>
                    <p className="text-medium text-subdued-foreground mt-0.5">properties enriched</p>
                  </div>
                </motion.div>
              )}
            {/* Complete — parent handles the reveal via BucketerResults */},
          </AnimatePresence>
        </motion.div>

        {/* Stats below preview — shown during enriching/bucketing */}
        <AnimatePresence>
          {!isComplete &&
            !isFailed &&
            (isBucketing || run.status === "enriching") &&
            (totalContacts > 0 || propertiesEnriched > 0) && (
              <motion.div
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease }}
                className="overflow-hidden"
              >
                <Column center wide>
                  <div className="flex items-center justify-center gap-10 mt-4">
                    <div className="text-center">
                      <span className="text-2xl font-bold text-foreground tabular-nums">
                        <AnimatedCounter value={totalContacts} />
                      </span>
                      <p className="text-medium text-subdued-foreground mt-0.5">contacts loaded</p>
                    </div>
                    <div className="w-px h-8 bg-foreground/6" aria-hidden />
                    <div className="text-center">
                      <span className="text-2xl font-bold text-foreground tabular-nums">
                        <AnimatedCounter value={propertiesEnriched} />
                      </span>
                      <p className="text-medium text-subdued-foreground mt-0.5">properties enriched</p>
                    </div>
                  </div>
                  <Column center wide noGap>
                    <Text sm subdued className="mt-4">
                      Tip: You can leave this page and return to it later to view your databank.
                    </Text>
                    <Text sm subdued>
                      It usually takes 2-3 minutes to enrich your contacts.
                    </Text>
                  </Column>
                </Column>
              </motion.div>
            )}
        </AnimatePresence>
      </motion.div>
      {devBar}
    </section>
  );
}
